import React from 'react';
import type { FC } from 'react';
import kebabCase from 'lodash/kebabCase.js';
import type { SelectOption } from '@wix/cli-ui-kit';
import { TextInput, ConfirmInput, SelectInput } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import {
  useCustomElementDirectoryValidation,
  useCustomElementNameValidation,
} from '../../../../dev-center/components/validations/index.js';
import { useWixCliAppBi } from '../../../../bi/index.js';
import { useExtensions } from '../../../extensions/useExtensions.js';
import { useWizardStep } from '../../Wizard.js';

export const ChooseNameStep: FC<{
  elementName?: string;
  onSubmit: (value: string) => void;
}> = ({ onSubmit, elementName }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { isActive, goToNextStep } = useWizardStep();
  const extensionType = t('generate_command.flow.custom_element').toLowerCase();
  const validateName = useCustomElementNameValidation();

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.custom_element.choose_name')}
      placeholder={
        elementName ?? t('generate_command.custom_element.name_placeholder')
      }
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.custom_element.choose_name'),
          questionKey: 'generate_command.custom_element.choose_name',
          answer: value,
          additionalInfo: {
            extensionType,
          },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateName}
    />
  );
};

export const ChooseFolderStep: FC<{
  elementName?: string;
  elementFolder?: string;
  onSubmit: (value: string) => void;
}> = ({ onSubmit, elementFolder, elementName }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { isActive, goToNextStep } = useWizardStep();
  const defaultElementFolder = kebabCase(elementName);
  const extensionType = t('generate_command.flow.custom_element').toLowerCase();
  const validateFolderName = useCustomElementDirectoryValidation('Site widget');

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.custom_element.choose_folder')}
      placeholder={elementFolder ?? defaultElementFolder}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.custom_element.choose_folder'),
          questionKey: 'generate_command.custom_element.choose_folder',
          answer: value,
          additionalInfo: {
            extensionType,
          },
        });

        onSubmit(value);
        goToNextStep();
      }}
      validate={validateFolderName}
    />
  );
};

export const LinkDashboardPageStep: FC<{
  onSubmit: (value: boolean) => void;
}> = ({ onSubmit }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { isActive, goToNextStep } = useWizardStep();
  const extensionType = t('generate_command.flow.custom_element').toLowerCase();

  return (
    <ConfirmInput
      isActive={isActive}
      label={t('generate_command.custom_element.link_dashboard_page')}
      onSubmit={(shouldLinkDashboardPage) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.custom_element.link_dashboard_page'),
          questionKey: 'generate_command.custom_element.link_dashboard_page',
          answer: String(shouldLinkDashboardPage),
          additionalInfo: { extensionType },
        });

        onSubmit(shouldLinkDashboardPage);
        goToNextStep();
      }}
    />
  );
};

export const ChooseDashboardPageStep: FC<{
  onSubmit: (value: string) => void;
}> = ({ onSubmit }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { getExtensions } = useExtensions();
  const { isActive, goToNextStep } = useWizardStep();
  const extensionType = t('generate_command.flow.custom_element').toLowerCase();
  const dashboardPages = getExtensions('DashboardPage');
  const dashboardPagesOptions: Array<SelectOption<string>> = dashboardPages.map(
    ({ id, title }) => ({
      value: id,
      title,
    })
  );

  return (
    <SelectInput<string>
      isActive={isActive}
      label={t('generate_command.custom_element.choose_dashboard_page')}
      options={dashboardPagesOptions}
      onSubmit={(option) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.custom_element.choose_dashboard_page'),
          questionKey: 'generate_command.custom_element.choose_dashboard_page',
          answer: option.title,
          additionalInfo: { extensionType },
        });
        onSubmit(option.value);
        goToNextStep();
      }}
    />
  );
};
